import {
  PageHeader,
  ActionTabs,
  ActionTabItem,
  ContentNugget,
  TypeChip,
  TypeChipProps,
  useContainerQuery,
} from '@backrs_ui/common-ui';

import { SuggestedNuggetsSidebar } from '@backrs_ui/grow-ui/src/components/SuggestedNuggetsSidebar/SuggestedNuggetsSidebar';

import { Box, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';

export type NuggetPageContentProps = Readonly<{
  title: string;
  subtitle: string;
  description: string;
  children: React.ReactNode;
  hasVisualMedia?: boolean;
  buttonGroup: ActionTabItem[];
  showCopySuccess?: boolean;
  onCopySuccessChange?: (show: boolean) => void;
  suggestedNuggets?: ContentNugget[];
  isLoadingSuggestedNuggets?: boolean;
  suggestedNuggetsError?: unknown;
  typeChipProps?: TypeChipProps[];
}>;

export const NuggetPageContent = ({
  title,
  subtitle,
  description,
  children,
  hasVisualMedia = true,
  buttonGroup,
  showCopySuccess,
  onCopySuccessChange,
  suggestedNuggets,
  isLoadingSuggestedNuggets,
  suggestedNuggetsError,
  typeChipProps,
}: NuggetPageContentProps) => {
  const [mainContentRef, isCompact] = useContainerQuery(600);

  return (
    <Box sx={containerSx}>
      <Box ref={mainContentRef} sx={mainContentSx}>
        {hasVisualMedia && (
          <Box sx={previewWrapperSx}>
            {children}
            <Box sx={floatingActionsSx}>
              <ActionTabs
                actions={buttonGroup}
                showCopySuccess={showCopySuccess}
                onCopySuccessChange={onCopySuccessChange}
              />
            </Box>
          </Box>
        )}
        <PageHeader title={title} subtitle={subtitle} showBorder={false} />
        {!hasVisualMedia && (
          <ActionTabs
            actions={buttonGroup}
            showCopySuccess={showCopySuccess}
            onCopySuccessChange={onCopySuccessChange}
          />
        )}
        {description && (
          <Typography
            sx={descriptionSx}
            variant={isCompact ? 'text-lg-regular' : 'text-xl-regular'}
          >
            {description}
          </Typography>
        )}
        {!hasVisualMedia && children}
        {typeChipProps && (
          <Box sx={footerContainerSx}>
            <Box sx={typeChipContainerSx}>
              {typeChipProps.map((typeChip, index) => (
                <TypeChip key={index} {...typeChip} />
              ))}
            </Box>
          </Box>
        )}
        {suggestedNuggets && suggestedNuggets.length > 0 && (
          <SuggestedNuggetsSidebar
            pageType="nuggetPage"
            nuggets={suggestedNuggets}
            isLoading={isLoadingSuggestedNuggets}
            error={suggestedNuggetsError}
          />
        )}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  width: '100%',
  margin: '0 auto',
  display: {
    xs: 'block',
    md: 'flex',
  },
  flexDirection: {
    xs: 'column',
    md: 'row',
  },
  minHeight: '100%',
};

const mainContentSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  maxWidth: {
    xs: '1240px',
    lg: '1240px',
  },
  margin: { sm: '0 auto' },
  pb: {
    xs: backrs.spacing['spacing-xl'],
    md: backrs.spacing['spacing-2xl'],
  },
  alignItems: 'left',
});

const previewWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  mb: backrs.spacing['spacing-2xl'],
});

const floatingActionsSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: backrs.spacing['spacing-2xl'],
  right: backrs.spacing['spacing-2xl'],
  zIndex: 1,
});

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/600'],
  lineHeight: '30px',
});

const typeChipContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-sm'],
  marginTop: backrs.spacing['spacing-2xl'],
});

const footerContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};
